/**
 * useInlineEdit Hook
 *
 * Feature 006: Admin Order Editing Without Page Reload
 *
 * Core inline edit state management hook implementing state machine:
 * idle → editing → saving → success/error → idle
 *
 * Supports queued edits during save operations for better UX.
 */

import { useState, useCallback, useEffect } from 'react';
import type { InlineEditState, InlineEditStatus } from '../types';

interface UseInlineEditOptions {
  initialValue: string;
  onSave: (value: string) => Promise<void>;
  validateFn?: (value: string) => string | null; // Return error message or null
}

interface UseInlineEditReturn {
  state: InlineEditState;
  value: string;
  handleChange: (newValue: string) => void;
  handleBlur: () => void;
  handleFocus: () => void;
  reset: () => void;
}

export function useInlineEdit({
  initialValue,
  onSave,
  validateFn
}: UseInlineEditOptions): UseInlineEditReturn {
  const [state, setState] = useState<InlineEditState>({
    status: 'idle',
    currentValue: initialValue,
    queuedValue: null,
    error: null
  });

  const [editingValue, setEditingValue] = useState<string>(initialValue);

  // Sync initial value changes from parent
  useEffect(() => {
    if (state.status === 'idle') {
      setState(prev => ({
        ...prev,
        currentValue: initialValue
      }));
      setEditingValue(initialValue);
    }
  }, [initialValue, state.status]);

  const handleChange = useCallback((newValue: string) => {
    setEditingValue(newValue);

    // Clear any previous errors when user types
    setState(prev => ({
      ...prev,
      error: null
    }));
  }, []);

  const handleFocus = useCallback(() => {
    setState(prev => ({
      ...prev,
      status: 'editing'
    }));
  }, []);

  const handleBlur = useCallback(async () => {
    const trimmedValue = editingValue.trim();

    // If value unchanged, return to idle
    if (trimmedValue === state.currentValue) {
      setState(prev => ({
        ...prev,
        status: 'idle',
        error: null
      }));
      return;
    }

    // Validate if validator provided
    if (validateFn) {
      const validationError = validateFn(trimmedValue);
      if (validationError) {
        setState(prev => ({
          ...prev,
          status: 'error',
          error: validationError
        }));
        return;
      }
    }

    // If already saving, queue this value
    if (state.status === 'saving') {
      setState(prev => ({
        ...prev,
        queuedValue: trimmedValue
      }));
      return;
    }

    // Start save process
    setState(prev => ({
      ...prev,
      status: 'saving',
      error: null
    }));

    try {
      await onSave(trimmedValue);

      // Save successful
      setState(prev => {
        const hasQueuedValue = prev.queuedValue !== null;

        if (hasQueuedValue) {
          // Process queued value immediately
          const queuedVal = prev.queuedValue!;
          setEditingValue(queuedVal);

          // Kick off another save for queued value
          setTimeout(() => {
            onSave(queuedVal).catch(err => {
              setState(p => ({
                ...p,
                status: 'error',
                error: err.message || 'Save failed'
              }));
            });
          }, 0);

          return {
            ...prev,
            status: 'saving',
            currentValue: trimmedValue,
            queuedValue: null
          };
        }

        // No queued value - show success briefly then return to idle
        return {
          ...prev,
          status: 'success',
          currentValue: trimmedValue,
          queuedValue: null
        };
      });

      // Show success state briefly (500ms) then return to idle
      if (!state.queuedValue) {
        setTimeout(() => {
          setState(prev => ({
            ...prev,
            status: 'idle'
          }));
        }, 500);
      }

    } catch (error) {
      // Save failed - show error and rollback value
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error instanceof Error ? error.message : 'Save failed',
        queuedValue: null
      }));

      // Rollback to last known good value
      setEditingValue(state.currentValue);
    }
  }, [editingValue, state.currentValue, state.status, state.queuedValue, validateFn, onSave]);

  const reset = useCallback(() => {
    setState({
      status: 'idle',
      currentValue: initialValue,
      queuedValue: null,
      error: null
    });
    setEditingValue(initialValue);
  }, [initialValue]);

  return {
    state,
    value: editingValue,
    handleChange,
    handleBlur,
    handleFocus,
    reset
  };
}
